import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { default as ReactSelect, components } from 'react-select';
import { DayValue, Day } from 'react-modern-calendar-datepicker';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import moment from 'moment';

import { showNotification } from '@/utils/notificationUtils';
import { useAddPet, useUploadPicture } from '../api/addPet';

import { FileUploader } from './FileUploader';
import { PetType } from './PetType';
import { Pet, PetWeightConfiguration } from '../types';
import './PetProfileForm.scss';

import './PetProfile.scss';
import { PetskedTitle } from '@/components/Heading/PetskedTitle';
import { Loader } from '@/components/Loader/Loader';
import { useGetPetSettings } from '../api/getPetSettings';
import { DropDown } from '@/components/dropdown/DropDown';
import Utils from '@/utils/util';
import { useTranslation } from 'react-i18next';
import DatePickerWithPortal from './DatepickerWithPortal';
type Props = {
  pet?: Pet;
  onChange?: (pet: Pet) => void;
  onSuccess: () => void;
  isEditMode: boolean;
  defaultPetType?: string;
};
export const PetProfileForm = ({ pet, onChange, onSuccess, isEditMode, defaultPetType }: Props) => {
  const petSettingsQuery = useGetPetSettings();
  const { t, i18n } = useTranslation();
  const addPetMutation = useAddPet();
  const uploadPictureMutation = useUploadPicture();
  const [breedList, setBreedList] = useState<any[]>();
  const [colorList, setColorList] = useState<any[]>();
  const [petSizeList, setPetSizeList] = useState<any[]>();

  const [data, setData] = useState<Pet>({
    name: '',
    breed: Utils.BREED_UNSPECIFIED,
    gender: '',
    weight: '',
    dateOfBirth: '',
    notes: '',
    petsImageUrl: '',
    id: '',
    petType: defaultPetType ? defaultPetType : '',
    petColor: '',
    petSize: '',
    fee: '',
    petAge: ''
  });

  useEffect(() => {
    pet && setData(pet);
  }, []);
  useEffect(() => {
    if (petSettingsQuery.data) {
      setBreedList(petSettingsQuery?.data?.breed_list.map((t: string) => ({ value: t, label: t })));
      setColorList(
        petSettingsQuery?.data?.pet_color_list.map((t: string) => ({ value: t, label: t }))
      );
      formatPetSize(petSettingsQuery?.data.pet_size_defaults);
    }
  }, [petSettingsQuery.data]);

  const formatPetSize = (petWeightConfig: PetWeightConfiguration) => {
    const pwCon: any[] = [];
    for (const [key, value] of Object.entries(petWeightConfig)) {
      pwCon.push({ value: key, label: value.name });
    }
    setPetSizeList(pwCon);
  };
  const [day, setDay] = React.useState<Day>({
    day: moment().date(),
    month: moment().month() + 1,
    year: moment().year(),
  } as Day);

  const onFormValueChange = (formFieldName: string, value: string) => {
    data[formFieldName as keyof typeof data] = value;
    setData({ ...data });
    onChange && onChange({ ...data });
  };

  const validate = () => {
    let errorMessage = undefined;
    const { name, dateOfBirth, breed, gender, weight, petColor, petSize } = data;
    if (!name || name.trim().length < 1) {
      errorMessage = t('Please enter pet name');
    } else if (!breed || breed.trim().length < 1) {
      errorMessage = 'Please select the pet breed';
    } 
    else if (!weight || weight.trim().length < 1) {
      errorMessage = 'Please enter pet weight';
    } else if (!dateOfBirth || dateOfBirth.trim().length < 1) {
      errorMessage = t('Please select pet date of birth');
    } else if (!gender || gender.trim().length < 1) {
      errorMessage = t('Please select gender of the pet');
    } else if (!petSize || petSize.trim().length < 1) {
      errorMessage = t('Please select pet size');
    }
    // else if (!petType || petType.trim().length < 1) {
    //   errorMessage = 'Please select pet type';
    // }
    else if (!petColor || petColor.trim().length < 1) {
      errorMessage = t('Please select color of the pet');
    }

    if (errorMessage) {
      showNotification({
        type: 'info',
        title: errorMessage,
      });
      return false;
    } else {
      return true;
    }
  };

  const onAddPetToServer = async () => {
    if (validate() && !addPetMutation.isLoading) {
      const {
        name,
        petType,
        breed,
        dateOfBirth,
        gender,
        weight,
        petColor,
        notes,
        petsImageUrl,
        id,
        petSize,
        petAge
      } = data;

      await addPetMutation.mutateAsync({
        data: {
          id: id ? parseInt(id) : undefined,
          name,
          dateOfBirth,
          weight,
          breed,
          gender,
          notes,
          petsImageUrl,
          petType,
          petColor,
          petSize,
          petAge
        },
      });
      onSuccess();
    }
  };

  const renderCustomInput = ({ ref }: any) => (
    <input
      readOnly
      ref={ref} // necessary
      placeholder={t('Date of Birth')}
      value={
        data?.dateOfBirth ? moment(data?.dateOfBirth).format('DD-MMM-YYYY') : 'Select date of birth'
      }
      className={`cust-input-mobile  ${data?.dateOfBirth ? '' : 'placeholder-text'}`}
    />
  );
  const onProfilePicUpload = async (formData: FormData) => {
    const response = await uploadPictureMutation.mutateAsync({
      file: formData,
    });
    //setImageUrl(response ? response.imageUrl : '');
    onFormValueChange('petsImageUrl', response ? response.imageUrl : '');
  };

  const onPetTypeChange = (petType: string) => {
    onFormValueChange('petType', petType);
  };
  const onPetSelect = (pet: Pet) => {
    setData(pet);
  };

  const Option = (props: any) => {
    return (
      <div>
        <components.Option {...props}>
          <div className="option-label">{props.label}</div>
        </components.Option>
      </div>
    );
  };

  return (
    <div className="pet-profile-form tab-content">
      {!isEditMode && (
        <PetskedTitle
          title={t('Add New Pet')}
          description={t('Add details of your new pet below')}
          size="sm"
          alignText="left"
        />
      )}
      <div className="scroller">
        {!isEditMode && (
          <FileUploader
            key={'pet-profile-pic'}
            onUpload={onProfilePicUpload}
            url={data.petsImageUrl}
            isUploading={uploadPictureMutation.isLoading}
          />
        )}
        <div className="petsked-form">
          <div className="form-field">
            <label className="form-label">{t('Select your pet type')}</label>
            <PetType
              onChange={onPetTypeChange}
              value={data.petType}
              defaultPetType={defaultPetType}
            />
          </div>
          <div className="form-field name">
            <label className="form-label">{t('Enter name and breed of your pet')}</label>
            <div className="split full pet">
              <input
                type="text"
                placeholder={t('Pet Name')}
                className="pet-name"
                value={data.name}
                onChange={(e: any) => onFormValueChange('name', e.target.value)}
              />
              <DropDown
                placeholder={t('Select Breed')}
                selectedValue={data?.breed}
                data={breedList ? breedList : []}
                onSelect={(value: any) => {
                  onFormValueChange('breed', value);
                }}
                onDismiss={() => {}}
                enableSearch={true}
              />
              
            </div>
            
          </div>
          <div className="form-field">
            <label className="form-label">{t('Enter some extra details below')}</label>
            <div className="split full pet sizelist">
            <input
                type="text"
                placeholder={t('Pet age')}
                className="pet-name"
                value={data.petAge}
                onChange={(e: any) => onFormValueChange('petAge', e.target.value)}
              />
              <DropDown
                placeholder={t('Select pet size')}
                selectedValue={data?.petSize}
                data={petSizeList ? petSizeList : []}
                onSelect={(value: any) => {
                  onFormValueChange('petSize', value);
                }}
                onDismiss={() => {}}
                enableSearch={false}
              />
             
            </div>
            <div className="split full pet">
              <DatePickerWithPortal
                value={data?.dateOfBirth ? data?.dateOfBirth : undefined}
                onChange={(val: DayValue) => {
                  console.log('val', val);
                  onFormValueChange('dateOfBirth', `${val?.month}/${val?.day}/${val?.year}`);
                }}
                placeholder={t('Select date of birth')}
                maximumDate={day}
              />
              <DropDown
                placeholder={t('Select color')}
                selectedValue={data?.petColor}
                data={colorList ? colorList : []}
                onSelect={(value: any) => {
                  onFormValueChange('petColor', value);
                }}
                onDismiss={() => {}}
                enableSearch={true}
              />
              {/* <ReactSelect
                classNamePrefix={'petsked-multiselect'}
                className="multiselect-react"
                isMulti={false}
                options={colorList ? colorList : []}
                placeholder={'Select Color'}
                components={{
                  Option,
                }}
                onChange={(selected: any) => {
                  onFormValueChange('petColor', selected.value);
                }}
                value={colorList?.find((cl) => cl.value == data?.petColor)}
                menuPlacement="auto"
              /> */}
            </div>
            <div className="split full">
              <span className="pet-weight-container">
                <input
                  type="text"
                  placeholder={t('Enter Weight')}
                  value={data.weight}
                  onChange={(e: any) => onFormValueChange('weight', e.target.value)}
                />
                <span>{t('lbs')}</span>
              </span>
              <div className="sex">
                <Form.Check
                  inline
                  value={'male'}
                  checked={data.gender?.toLowerCase() === 'male' ? true : false}
                  label={t('Male')}
                  name="group1"
                  type="radio"
                  onChange={(e: any) => onFormValueChange('gender', e.target.value)}
                />
                <span className="seperator"></span>
                <Form.Check
                  inline
                  value={'female'}
                  checked={data.gender?.toLowerCase() === 'female' ? true : false}
                  label={t('Female')}
                  name="group1"
                  type="radio"
                  onChange={(e: any) => onFormValueChange('gender', e.target.value)}
                />
              </div>
            </div>
            <textarea
              rows={4}
              value={data.notes}
              placeholder={t('Add note')}
              onChange={(e: any) => onFormValueChange('notes', e.target.value)}
            />
          </div>
          <div className="action">
            <button className="petsked-button button-dark" onClick={onAddPetToServer}>
              {addPetMutation.isLoading ? (
                <Loader />
              ) : (
                <span>{isEditMode ? t('Update Profile') : t('Add New Pet')}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
